import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import '../styles/Dashboard.css';

const Dashboard = () => {
  const [projectData, setProjectData] = useState({
    name: '',
    projectInfo: '',
    projectDescription: '',
    images: []
  });

  const [careerData, setCareerData] = useState({
    title: '',
    description: ''
  });

  const [projects, setProjects] = useState([]);
  const [careers, setCareers] = useState([]);
  const [message, setMessage] = useState('');
  const [editingProject, setEditingProject] = useState(null);
  const [editingCareer, setEditingCareer] = useState(null);

  // Get token function
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    console.log('Current token:', token);
    
    if (!token) {
      setMessage('Please login first');
      return null;
    }
    return { 'Authorization': `Bearer ${token}` };
  };

  useEffect(() => {
    fetchProjects();
    fetchCareers();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Fetch projects error:', error);
      setMessage('Error fetching projects');
    }
  };

  const fetchCareers = async () => {
    try {
      const response = await axios.get(`${API_URL}/careers`);
      setCareers(response.data);
    } catch (error) {
      console.error('Fetch careers error:', error);
      setMessage('Error fetching careers');
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Please log in to perform this action');
        return;
      }

      const formData = new FormData();
      formData.append('name', projectData.name);
      const fullDescription = `${projectData.projectInfo}\n${projectData.projectDescription}`;
      formData.append('description', fullDescription);
      
      if (projectData.images.length > 0) {
        for (let i = 0; i < projectData.images.length; i++) {
          formData.append('images', projectData.images[i]);
        }
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };

      console.log('Project operation - Headers:', headers);
      console.log('Project data being sent:', projectData);

      let response;
      if (editingProject) {
        console.log('Updating project:', editingProject._id);
        response = await axios({
          method: 'put',
          url: `${API_URL}/projects/${editingProject._id}`,
          data: formData,
          headers: headers
        });
        console.log('Update response:', response.data);
        setMessage('Project updated successfully!');
        setEditingProject(null);
      } else {
        response = await axios.post(
          `${API_URL}/projects`,
          formData,
          { headers: headers }
        );
        console.log('Create response:', response.data);
        setMessage('Project added successfully!');
      }

      setProjectData({ name: '', projectInfo: '', projectDescription: '', images: [] });
      fetchProjects();
    } catch (error) {
      console.error('Project operation error:', error.response || error);
      setMessage(error.response?.data?.message || 'Error processing project');
    }
  };

  const handleCareerSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Please log in to perform this action');
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      console.log('Career operation - Headers:', headers);
      console.log('Career data being sent:', careerData);

      if (editingCareer) {
        console.log('Updating career:', editingCareer._id);
        const response = await axios.put(
          `${API_URL}/careers/${editingCareer._id}`,
          careerData,
          { headers }
        );
        console.log('Update response:', response.data);
        setMessage('Career updated successfully!');
        setEditingCareer(null);
      } else {
        const response = await axios.post(
          `${API_URL}/careers`,
          careerData,
          { headers }
        );
        console.log('Create response:', response.data);
        setMessage('Career added successfully!');
      }

      setCareerData({ title: '', description: '' });
      fetchCareers();
    } catch (error) {
      console.error('Career operation error:', error.response || error);
      setMessage(error.response?.data?.message || 'Error processing career');
    }
  };

  const handleDeleteProject = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Please log in to delete projects');
        return;
      }

      console.log('Attempting to delete project with ID:', id);
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      const response = await axios.delete(
        `${API_URL}/projects/${id}`,
        { headers }
      );
      console.log('Delete project response:', response.data);
      setMessage('Project deleted successfully!');
      fetchProjects();
    } catch (error) {
      console.error('Delete project error:', error.response || error);
      setMessage(error.response?.data?.message || 'Error deleting project');
    }
  };

  const handleDeleteCareer = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Please log in to delete careers');
        return;
      }

      console.log('Attempting to delete career with ID:', id);
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      const response = await axios.delete(
        `${API_URL}/careers/${id}`,
        { headers }
      );
      console.log('Delete career response:', response.data);
      setMessage('Career deleted successfully!');
      fetchCareers();
    } catch (error) {
      console.error('Delete career error:', error.response || error);
      setMessage(error.response?.data?.message || 'Error deleting career');
    }
  };

  const handleEditProject = (project) => {
    const [projectInfo, projectDescription] = project.description.split('\n');
    setProjectData({
      name: project.name,
      projectInfo,
      projectDescription: projectDescription || '',
      images: []
    });
    setEditingProject(project);
  };

  const handleEditCareer = (career) => {
    setCareerData({
      title: career.title,
      description: career.description
    });
    setEditingCareer(career);
  };

  const handleImageChange = (e) => {
    if (e.target.files.length > 4) {
      alert('You can only upload up to 4 images');
      return;
    }
    setProjectData({ ...projectData, images: e.target.files });
  };

  return (
    <section className="dashboard-section">
      <div className="container">
        <div className="dashboard-header">
          <h2 className="section-title">Admin Dashboard</h2>
          {message && (
            <div className="alert-message">
              {message}
            </div>
          )}
        </div>

        <div className="dashboard-content">
          {/* Project Form */}
          <div className="form-wrapper">
            <div className="form-container">
              <h3>{editingProject ? 'Edit Project' : 'Add New Project'}</h3>
              <form onSubmit={handleProjectSubmit}>
                <div className="form-group">
                  <label>Project Name</label>
                  <input
                    type="text"
                    value={projectData.name}
                    onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Project Information</label>
                  <textarea
                    value={projectData.projectInfo}
                    onChange={(e) => setProjectData({ ...projectData, projectInfo: e.target.value })}
                    className="form-control"
                    rows="3"
                    placeholder="Brief overview of the project"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Project Description</label>
                  <textarea
                    value={projectData.projectDescription}
                    onChange={(e) => setProjectData({ ...projectData, projectDescription: e.target.value })}
                    className="form-control"
                    rows="4"
                    placeholder="Detailed description of the project"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Images (Max 4)</label>
                  <input
                    type="file"
                    onChange={handleImageChange}
                    className="form-control"
                    multiple
                    accept="image/*"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  {editingProject ? 'Update Project' : 'Add Project'}
                </button>
                {editingProject && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditingProject(null);
                      setProjectData({ name: '', projectInfo: '', projectDescription: '', images: [] });
                    }}
                  >
                    Cancel Edit
                  </button>
                )}
              </form>
            </div>

            {/* Projects List */}
            <div className="items-list">
              <h3>Existing Projects</h3>
              {projects.map(project => (
                <div key={project._id} className="list-item">
                  <span>{project.name}</span>
                  <div className="item-actions">
                    <button onClick={() => handleEditProject(project)}>Edit</button>
                    <button onClick={() => handleDeleteProject(project._id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Career Form */}
          <div className="form-wrapper">
            <div className="form-container">
              <h3>{editingCareer ? 'Edit Career' : 'Add New Career'}</h3>
              <form onSubmit={handleCareerSubmit}>
                <div className="form-group">
                  <label>Job Title</label>
                  <input
                    type="text"
                    value={careerData.title}
                    onChange={(e) => setCareerData({ ...careerData, title: e.target.value })}
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Job Description</label>
                  <textarea
                    value={careerData.description}
                    onChange={(e) => setCareerData({ ...careerData, description: e.target.value })}
                    className="form-control"
                    rows="4"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  {editingCareer ? 'Update Career' : 'Add Career'}
                </button>
                {editingCareer && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditingCareer(null);
                      setCareerData({ title: '', description: '' });
                    }}
                  >
                    Cancel Edit
                  </button>
                )}
              </form>
            </div>

            {/* Careers List */}
            <div className="items-list">
              <h3>Existing Careers</h3>
              {careers.map(career => (
                <div key={career._id} className="list-item">
                  <span>{career.title}</span>
                  <div className="item-actions">
                    <button onClick={() => handleEditCareer(career)}>Edit</button>
                    <button onClick={() => handleDeleteCareer(career._id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Dashboard;